import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useFormik} from "formik";
import {Button} from "flowbite-react";
import {Edit, TrashBin} from "flowbite-react-icons/outline";
import Modal from "react-modal";
import {Check, X as XIcon} from "react-feather";
import {
    adminAddEnvironment,
    adminDeleteEnvironment,
    adminGetEnvironments,
    adminUpdateEnvironment
} from '../../../services/api/admin';
import PaginatedTable from "../../Block/Table/PaginatedTable";
import LoadSpinner from "../../Common/LoadSpinner";
import ListPagination from "../../Block/Pagination/ListPagination";
import ConfirmationModal from "../../Modal/ConfirmationModal";
import ModalAddEditEnvironment from "../../Modal/ModalAddEditEnvironment";
import TextInput from "../../Formik/TextInput";
import {PLATFORMS} from "../../../services/constants/EnvironmentPlatforms";

const EnvironmentList = () => {
    const headers = [
        {data: 'Key', classes: 'sl-border-right sl-font-bold'},
        {data: 'Value', classes: 'sl-border-right sl-font-bold'},
        {data: 'Default', classes: 'sl-border-right sl-font-bold'},
        {data: 'Platform', classes: 'sl-font-bold'},
    ];

    const [items, setItems] = useState([]);
    const [fetched, setFetched] = useState(false);
    const [addEditError, setAddEditError] = useState('');
    const [deleteError, setDeleteError] = useState('');

    const filterFormik = useFormik({
        initialValues: {search_term: "", platform: ""},
        onSubmit: () => onClickFilter()
    });

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const navigate = useNavigate();
    const {per_page} = useParams();
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        count: 0,
        per_page: parseInt(per_page, 10) || 10,
        current_page: 1,
        total_pages: 1
    });

    const [activeFilters, setActiveFilters] = useState({
        page: pagination.current_page,
        per_page: pagination.per_page,
        search_term: '',
        platform: ''
    });

    const fetchEnvironments = async () => {
        if (!activeFilters?.page || !(parseInt(activeFilters?.per_page) > 0)) return;
        if (items.length || fetched) return;

        try {
            const result = await adminGetEnvironments(activeFilters);
            if (!result) return;

            const {data, meta} = result;
            const transformed = data.map((item) => {
                return {
                    key: {data: item.key, classes: "sl-min-w-10 sl-max-w-64 sl-break-all"},
                    value: {data: item.value, classes: "sl-max-w-64 sl-break-all"},
                    is_default: {
                        data: item.is_default
                            ? <Check size={18} color="#16a34a" className={"sl-m-auto"}/>
                            : <XIcon size={18} color="#9ca3af" className={"sl-m-auto"}/>,
                        classes: "sl-text-center"
                    },
                    platform: {data: item.platform, classes: "sl-min-w-10"},
                    edit: {
                        data: <Edit size={20} color={"#344054"} className={"sl-m-auto"}/>,
                        onClick: () => handleToggleEdit(item),
                        classes: "sl-action-edit sl-cursor-pointer"
                    },
                    delete: {
                        data: <TrashBin size={20} color={"#344054"} className={"sl-m-auto"}/>,
                        onClick: () => handleToggleDelete(item),
                        classes: "sl-action-delete sl-border-right sl-cursor-pointer"
                    }
                };
            });

            setFetched(true);
            setItems(transformed);
            setPagination(meta.pagination);

            if (
                meta.pagination.current_page !== activeFilters.page ||
                meta.pagination.per_page !== activeFilters.per_page
            ) {
                setActiveFilters({
                    ...activeFilters,
                    page: meta.pagination.current_page,
                    per_page: meta.pagination.per_page
                });
            }
        } catch (e) {
            console.error('Error fetching environments:', e);
        }
    };

    const handleToggleAdd = () => {
        setAddOpen(!addOpen);
        setAddEditError('');
    };

    const handleAdd = async ({payload}) => {
        const response = await adminAddEnvironment(payload);
        if (response?.status === 201 || response?.status === 200) {
            handleToggleAdd();
            onClickReset();
            setAddEditError('');
        } else {
            setAddEditError(response?.data?.message || 'Failed to add feature flag');
        }
    };

    const handleToggleEdit = (item) => {
        setEditOpen(!editOpen);
        setSelected(item);
        setAddEditError('');
    };

    const handleEdit = async ({id, payload}) => {
        const response = await adminUpdateEnvironment(id, payload);
        if (response?.status === 200) {
            handleToggleEdit();
            onClickReset();
            setAddEditError('');
        } else {
            setAddEditError(response?.data?.message || 'Failed to update feature flag');
        }
    };

    const handleToggleDelete = (item) => {
        setDeleteOpen(!deleteOpen);
        setSelected(item);
        setDeleteError('');
    };

    const handleDelete = async (item) => {
        const response = await adminDeleteEnvironment(item.id);
        if (response?.status === 200 || response?.status === 204) {
            setDeleteOpen(false);
            setDeleteError('');
            onClickReset();
        } else {
            setDeleteError(response?.data?.message || 'Failed to delete feature flag');
        }
    };

    useEffect(() => {
        fetchEnvironments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    useEffect(() => {
        setActiveFilters({
            ...activeFilters,
            page: page || 1,
            per_page: per_page || 10
        });
        setItems([]);
        setFetched(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, per_page]);

    const onClickFilter = () => {
        setActiveFilters({
            ...activeFilters,
            search_term: filterFormik.values.search_term?.trim() || '',
            platform: filterFormik.values.platform || '',
            page: 1,
            per_page: 10
        });
        setItems([]);
        setFetched(false);
        navigate(`/admin/environment`);
    };

    const onClickReset = () => {
        setActiveFilters({
            page: 1,
            per_page: 10,
            search_term: '',
            platform: ''
        });
        setPage(1);
        setItems([]);
        setFetched(false);
        filterFormik.setFieldValue('search_term', '');
        filterFormik.setFieldValue('platform', '');
        navigate(`/admin/environment`);
    };

    return (
        <div>
            <main className="sl-main sl-mt-8">
                <div className="sl-container sl-m-auto sl-my-8 sl-flex sl-flex-col sl-gap-3">
                    <div className={"sl-flex sl-flex-row sl-justify-space-between sl-items-end sl-gap-2"}>
                        <form
                            onSubmit={filterFormik.handleSubmit}
                            className={"sl-flex sl-flex-row sl-gap-2 sl-items-end"}
                        >
                            <div className={"sl-min-w-72"}>
                                <TextInput formik={filterFormik} label={'Key'} name={'search_term'}/>
                            </div>
                            <div className={"sl-min-w-40"}>
                                <span className="sl-font sl-font-medium sl-text-sm sl-text-gray-700">Platform</span>
                                <div className="sl-mt-2 sl-input sl-border-1 sl-shadow-xs sl-rounded-lg sl-w-fill-available sl-flex sl-relative">
                                    <select
                                        name="platform"
                                        value={filterFormik.values.platform}
                                        onChange={(e) => filterFormik.setFieldValue('platform', e.target.value)}
                                        className="sl-input sl-border-none sl-rounded-lg sl-font sl-text-md sl-font-normal sl-m-0 sl-h-11 sl-w-fill-available sl-pl-2 sl-pr-8"
                                    >
                                        <option value="">All platforms</option>
                                        {PLATFORMS.map((p) => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className={"sl-flex sl-gap-2"}>
                                <Button type="submit" color="blue">Filter</Button>
                                <Button type="button" color="gray" onClick={onClickReset}>Reset</Button>
                            </div>
                        </form>

                        <Button color="blue" onClick={handleToggleAdd}>Add new</Button>
                    </div>

                    {deleteOpen && selected && <ConfirmationModal
                        title={`Delete feature flag`}
                        message={`Are you sure you want to delete feature flag "${selected.key}" (${selected.platform})?`}
                        btnConfirm={'Delete'}
                        errorMessage={deleteError}
                        handleConfirm={async () => handleDelete(selected)}
                        handleClose={() => {
                            setDeleteOpen(false);
                            setSelected(null);
                            setDeleteError('');
                        }}
                    />}

                    {selected && <Modal
                        isOpen={editOpen}
                        onRequestClose={handleToggleEdit}
                        className="sl-modal-content sl-max-h-185"
                        overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">
                        <ModalAddEditEnvironment
                            item={selected}
                            handleAction={handleEdit}
                            onHandleToggle={handleToggleEdit}
                            title={"Edit feature flag"}
                            errorMessage={addEditError}
                            message={`Update the feature flag "${selected.key}":`}
                        />
                    </Modal>}

                    {addOpen && <Modal
                        isOpen={addOpen}
                        onRequestClose={handleToggleAdd}
                        className="sl-modal-content sl-max-h-185"
                        overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">
                        <ModalAddEditEnvironment
                            handleAction={handleAdd}
                            onHandleToggle={handleToggleAdd}
                            title={"Add new feature flag"}
                            errorMessage={addEditError}
                            message={'Provide the following details to add a new feature flag:'}
                        />
                    </Modal>}

                    {fetched ? (
                        <div className="sl-table-container">
                            <PaginatedTable headers={headers} data={items}/>
                        </div>
                    ) : (
                        <div className="sl-self-center sl-mt-3 sl-mb-21">
                            <LoadSpinner/>
                        </div>
                    )}
                    {pagination.total_pages > 1 && fetched && (
                        <ListPagination
                            paginationData={pagination}
                            onPageChange={(newPage) => setPage(newPage)}
                        />
                    )}
                </div>
            </main>
        </div>
    );
};

export default EnvironmentList;
